import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LeverageSlider } from "@/features/leverage/leverage-slider";
import { cn, formatCurrency } from "@/lib/utils";
import type { StorePosition } from "@/backend/store";
import { PositionsApi } from "@/backend/api-client";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useAccountStore } from "@/backend/store";
import { useTradingSettings } from "@/features/trading-settings/trading-settings-context";
import { toast } from "sonner";
import { 
  fetchLeverageConfig, 
  getMaxPositionFromConfig, 
  type LeverageConfig 
} from "@/features/trading-settings/types";

type EditLeverageModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: StorePosition | null;
  onSuccess?: () => void;
};

export const EditLeverageModal = ({
  open,
  onOpenChange,
  position,
}: EditLeverageModalProps) => {
  const { userId } = useUnifiedAuth();
  const { setLeverage } = useTradingSettings();
  const [selectedLeverage, setSelectedLeverage] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leverageConfig, setLeverageConfig] = useState<LeverageConfig | null>(null);
  
  // Fetch leverage config when modal opens or market changes
  useEffect(() => {
    if (open && position?.market) {
      fetchLeverageConfig(position.market).then(setLeverageConfig);
    }
  }, [open, position?.market]);

  // Get max leverage from API config, or fall back to system absolute maximum
  const maxLeverage = leverageConfig?.max_leverage || 100;

  // Calculate max position based on leverage tier
  const maxPosition = useMemo(() => {
    return getMaxPositionFromConfig(selectedLeverage, leverageConfig);
  }, [selectedLeverage, leverageConfig]);

  // Generate dynamic leverage presets based on max leverage
  const leveragePresets = useMemo(() => {
    const basePresets = [1, 2, 5, 10, 25, 50, 100];
    // Filter to only show presets <= maxLeverage
    const filtered = basePresets.filter(lev => lev <= maxLeverage);
    // Always include maxLeverage if not already present
    if (!filtered.includes(maxLeverage)) {
      filtered.push(maxLeverage);
      filtered.sort((a, b) => a - b);
    }
    return filtered;
  }, [maxLeverage]);

  // Reset to position's current leverage when modal opens
  useEffect(() => {
    if (open && position) {
      setSelectedLeverage(position.leverage || 1);
    }
  }, [open, position]);

  // Clamp selected leverage if it exceeds max
  useEffect(() => {
    if (selectedLeverage > maxLeverage) {
      setSelectedLeverage(maxLeverage);
    }
  }, [maxLeverage, selectedLeverage]);
  
  const currentLeverage = position?.leverage || 1;
  const hasChanges = selectedLeverage !== currentLeverage;
  
  // Calculate new margin and margin change
  const positionValue = position ? Math.abs(position.size) * position.entryPrice : 0;
  const currentMargin = position?.margin || 0;
  const newMargin = positionValue / selectedLeverage;
  const marginChange = newMargin - currentMargin;
  
  const handleSubmit = useCallback(async () => {
    if (!userId || !position || !hasChanges) return;
    
    setIsSubmitting(true);
    try {
      const response = await PositionsApi.updateLeverage(
        position.market,
        selectedLeverage
      );
      
      // Sync updated position to store for immediate UI update
      // The response already includes the updated position - use it directly!
      // This immediately updates the UI without needing a refetch
      if (response.position) {
        useAccountStore.getState().updatePositionFromHttp(response.position);
      }
      
      // Also update the order form's leverage setting to keep them in sync
      setLeverage(position.market, selectedLeverage);
      
      toast.success(response.message);
      // No need to call onSuccess (which triggers refetch) - store is already updated
      // and LeverageCell subscribes to the store directly, so it will re-render automatically
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update leverage";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [userId, position, selectedLeverage, hasChanges, onOpenChange, setLeverage]);
  
  if (!position) return null;
  
  const isLong = position.side === "long";
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Change Leverage
            <span className={cn(
              "text-sm font-normal px-2 py-0.5 rounded",
              isLong ? "bg-bid/20 text-bid-foreground" : "bg-ask/20 text-ask-foreground"
            )}>
              {position.market} {isLong ? "Long" : "Short"}
            </span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          {/* Current vs New Leverage */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Current Leverage</span>
            <span className="font-medium">{currentLeverage}x</span>
          </div>
          
          {/* Leverage Selector with Slider and Presets */}
          <div className="space-y-2">
            {/* Slider */}
            <LeverageSlider
              value={selectedLeverage}
              onChange={setSelectedLeverage}
              max={maxLeverage}
            />
            
            {/* Preset buttons - styled to match Adjust Margin modal */}
            <div className="flex gap-2 mt-3">
              {leveragePresets.map((lev) => (
                <button
                  key={lev}
                  onClick={() => setSelectedLeverage(lev)}
                  className={cn(
                    "flex-1 py-1.5 text-xs font-medium rounded border transition-colors",
                    selectedLeverage === lev
                      ? "border-buy bg-buy/10 text-buy"
                      : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                  )}
                >
                  {lev}x
                </button>
              ))}
            </div>
          </div>

          {/* Maximum position info */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Maximum position at {selectedLeverage}x</span>
            <span className="font-medium">{formatCurrency(maxPosition, 0)} USD</span>
          </div>
          
          {/* Margin Info - Clean style matching Adjust Margin modal */}
          <div className="space-y-2.5 pt-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Current Margin</span>
              <span className="font-medium">${formatCurrency(currentMargin, 2)}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">New Margin</span>
              <span className="font-medium">
                {hasChanges ? `$${formatCurrency(newMargin, 2)}` : "—"}
              </span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {marginChange > 0 ? "Additional Required" : "Margin Released"}
              </span>
              <span className={cn(
                "font-medium",
                !hasChanges ? "text-muted-foreground" : marginChange > 0 ? "text-ask-foreground" : "text-bid-foreground"
              )}>
                {hasChanges ? `${marginChange > 0 ? "+" : "-"}$${formatCurrency(Math.abs(marginChange), 2)}` : "—"}
              </span>
            </div>
          </div>
          
          {/* Shortened help text - one line */}
          <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md">
            Leverage affects margin requirements and liquidation distance.
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-11"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 h-11 bg-buy hover:bg-buy/90 text-white"
              disabled={!hasChanges || isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Confirm"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
